import { CmdModal, CmdModalFooterButton, CmdRadioButton } from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { BluecoinUsageMethod } from '../interfaces';
import { BluecoinRecord, BluecoinRecordCustomer } from '../List/BluecoinsListState';
import { updateMethod } from '../BluecoinsService';
import PermissionsService, {
  PERMISSION_BLUECOINS_USE_CREATION_SETTINGS,
} from '../../../Services/PermissionsService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import useAnalytics from '../../../hooks/useAnalytics';

interface UseCreationSettings {
  data: BluecoinRecord;
  closeCallback: React.Dispatch<React.SetStateAction<boolean>>;
  updateTable: () => void;
}

// eslint-disable-next-line react/function-component-definition
const UseCreationSettings = (props: UseCreationSettings): JSX.Element => {
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const { formatMessage: f } = useIntl();
  const documentTitle = `${f({ id: 'bluecoin.modal.useCreationSettings.title' })} - ${f({
    id: 'menu.bluecoins',
  })}`;
  useDocumentTitle(documentTitle);
  const { trackEvent } = useAnalytics();
  const { handleSubmit, control, setValue, watch } = useForm<BluecoinUsageMethod>({
    mode: 'onChange',
    defaultValues: {
      usageMethodStart: props.data.usageMethodStart,
      usageMethodEnd: props.data.usageMethodEnd,
    },
  });

  const handleOnSubmit = async (values: BluecoinUsageMethod) => {
    trackEvent([`bluecoin form`, 'click', 'Save usage creation settings']);
    const response = await updateMethod(
      props.data.id,
      values.usageMethodStart,
      values.usageMethodEnd
    );
    if (response) {
      props.updateTable();
      props.closeCallback(true);
      return true;
    }
    return false;
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button e-button--disabled',
      title: f({ id: 'bluecoin.modal.button.close' }),
      closeCallback: props.closeCallback,
    },
  ];

  // const customerIds: number[] = props.data.customers.map((customer) => customer.id);

  if (
    PermissionsService.hasAccess(
      PERMISSION_BLUECOINS_USE_CREATION_SETTINGS,
      ['update'],
      props.data.customers
        .map((customer: BluecoinRecordCustomer) => customer.id)
        .filter((customerId: number) => selectedCustomers.includes(customerId))
    )
  ) {
    buttons.push({
      id: 'save-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.modal.button.save' }),
      submitCallback: handleSubmit(handleOnSubmit),
    });
  }

  // let isUsageMethodStartDisabled = !PermissionsService.hasAccess(PERMISSION_BLUECOINS_USE_CREATION_SETTINGS, ['update']);
  const isUsageMethodStartDisabled = !PermissionsService.hasAccess(
    PERMISSION_BLUECOINS_USE_CREATION_SETTINGS,
    ['update'],
    props.data.customers
      .map((customer: BluecoinRecordCustomer) => customer.id)
      .filter((customerId: number) => selectedCustomers.includes(customerId))
  );

  // let isUsageMethodEndDisabled = !PermissionsService.hasAccess(PERMISSION_BLUECOINS_USE_CREATION_SETTINGS, ['update']);
  const isUsageMethodEndDisabled = !PermissionsService.hasAccess(
    PERMISSION_BLUECOINS_USE_CREATION_SETTINGS,
    ['update'],
    props.data.customers
      .map((customer: BluecoinRecordCustomer) => customer.id)
      .filter((customerId: number) => selectedCustomers.includes(customerId))
  );

  // if (watch('usageMethodStart') === 'signal' && watch('usageMethodEnd') === 'move') {
  //   // isUsageMethodEndDisabled = true;
  //   const saveButton = document.getElementById('save-button');
  //   saveButton && saveButton.setAttribute('disabled', 'true');
  // } else {
  //   const saveButton = document.getElementById('save-button');
  //   saveButton && saveButton.getAttribute('disabled') && saveButton.removeAttribute('disabled');
  // }

  // const methodStartSignal = document.getElementById('radio-usageMethodStart-0');
  // const methodStartMove = document.getElementById('radio-usageMethodStart-1');
  // const methodEndSignal = document.getElementById('radio-usageMethodEnd-0');

  const methodEndMove = document.getElementById('radio-usageMethodEnd-1');
  const saveButton = document.getElementById('save-button');

  React.useEffect(() => {
    const methodEndMoveElement = document.getElementById('radio-usageMethodEnd-1');
    const saveButtonElement = document.getElementById('save-button');

    if (watch('usageMethodStart') === 'signal' && methodEndMoveElement) {
      methodEndMoveElement.setAttribute('disabled', 'true');
    } else if (methodEndMoveElement && methodEndMoveElement.getAttribute('disabled')) {
      methodEndMoveElement.removeAttribute('disabled');
    }
    if (watch('usageMethodStart') === 'signal' && watch('usageMethodEnd') === 'move') {
      if (saveButtonElement) {
        saveButtonElement.setAttribute('disabled', 'true');
      }
    } else if (saveButtonElement && saveButtonElement.getAttribute('disabled')) {
      saveButtonElement.removeAttribute('disabled');
    }
  }, [watch('usageMethodEnd')]);

  if (watch('usageMethodStart') === 'signal' && methodEndMove) {
    methodEndMove.setAttribute('disabled', 'true');
  } else if (methodEndMove && methodEndMove.getAttribute('disabled')) {
    methodEndMove.removeAttribute('disabled');
  }
  if (watch('usageMethodStart') === 'signal' && watch('usageMethodEnd') === 'move') {
    if (saveButton) {
      saveButton.setAttribute('disabled', 'true');
    }
  } else if (saveButton && saveButton.getAttribute('disabled')) {
    saveButton.removeAttribute('disabled');
  }

  // console.log('methodEndMove: ', methodEndMove);
  // console.log('isUsageMethodEndDisabled: ', isUsageMethodEndDisabled);

  return (
    <CmdModal
      id="bluecoin-use-creation-settings"
      title={f({ id: 'bluecoin.modal.useCreationSettings.title' })}
      footerButtons={buttons}
      usePortal={true}
      width="540px"
    >
      <div>
        <Controller
          name="usageMethodStart"
          control={control}
          render={({ field }) => (
            <CmdRadioButton
              {...field}
              id="usageStart"
              label={f({ id: 'bluecoin.modal.useCreationSettings.form.usageStart.label' })}
              options={[
                {
                  value: 'signal',
                  item: f({ id: 'bluecoin.modal.useCreationSettings.form.usageStart.signal' }),
                },
                {
                  value: 'move',
                  item: f({ id: 'bluecoin.modal.useCreationSettings.form.usageStart.move' }),
                },
              ]}
              required={true}
              disabled={isUsageMethodStartDisabled}
              setValue={(value: 'move' | 'signal') => {
                setValue('usageMethodStart', value);
                trackEvent([`bluecoin form`, 'click', 'Select usage method start']);
              }}
            />
          )}
        />
        <Controller
          name="usageMethodEnd"
          control={control}
          render={({ field }) => (
            <CmdRadioButton
              {...field}
              id="usageEnd"
              label={f({ id: 'bluecoin.modal.useCreationSettings.form.usageEnd.label' })}
              options={[
                {
                  value: 'signal',
                  item: f({ id: 'bluecoin.modal.useCreationSettings.form.usageEnd.signal' }),
                },
                {
                  value: 'move',
                  item: f({ id: 'bluecoin.modal.useCreationSettings.form.usageEnd.move' }),
                },
              ]}
              required={true}
              disabled={isUsageMethodEndDisabled}
              setValue={(value: 'move' | 'signal') => {
                setValue('usageMethodEnd', value);
                trackEvent([`bluecoin form`, 'click', 'Select usage method end']);
              }}
            />
          )}
        />
      </div>
    </CmdModal>
  );
};

export default UseCreationSettings;
